import { usePostCoachSchedules } from '#api/schedules';
import { Button, Flex, Input } from '#atoms';
import { PATH } from '#const/path';
import useOnboardingCompleteMutation from '#hooks/queries/onboard/useOnboardingCompleteMutation';
import { FloatButtonContainer } from '#molecules';
import { useToast } from '#providers/ToastProvider';
import { useState } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import CheckBoxAtom from 'src/components/atoms/Checkbox';
import styled from 'styled-components';
import { useOnBoardStep } from '../../OnBoardStep.hooks';
import {
  differentSchedulesSelector,
  gymStateAtom,
  isSameStateAtom,
  sameSchedulesSelector
} from '../Step6/Step6.atoms';
import DifferentSchedule from '../Step6/components/DifferentSchedule';
import SameSchedule from '../Step6/components/SameSchedule';
import * as S from '../Step6/styles';

export default function Step9() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setStep } = useOnBoardStep();
  const [isSameState, setIsSameState] = useRecoilState(isSameStateAtom);
  const sameSchedules = useRecoilValue(sameSchedulesSelector);
  const differentSchedules = useRecoilValue(differentSchedulesSelector);
  const { mutateAsync: completeOnboarding, isLoading: isCompleteLoading } =
    useOnboardingCompleteMutation();
  const { mutateAsync: postCoashSchedules, isLoading: isPostSchedulesLoading } =
    usePostCoachSchedules();
  const [gymState, setGymState] = useRecoilState(gymStateAtom);
  const [isDeleteGym, setDeleteGym] = useState(true);

  const nextButtonDisabled =
    !gymState ||
    (isSameState ? sameSchedules.length < 1 : differentSchedules.length < 1) ||
    isCompleteLoading ||
    isPostSchedulesLoading;

  const handleNext = async () => {
    try {
      await postCoashSchedules(
        isSameState ? sameSchedules : differentSchedules
      );
      await completeOnboarding();
      TagManager.dataLayer({
        dataLayer: {
          event: 'onboarding_schedlocset_next_click',
          category: '신규가입 온보딩',
          action: 'onboarding_schedlocset_next_click',
          value: 2
        }
      });

      navigate(PATH.ONBOARD_COMPLETE);
      showToast({
        message: '수업 일정이 저장되었습니다.',
        type: 'success'
      });
    } catch (err: any) {
      showToast({
        message:
          err.response?.data.message || '수업 일정을 저장하는데 실패했습니다.',
        type: 'fail'
      });
    }
  };

  const handleDeleteGym = () => {
    setGymState(null);
    setDeleteGym(!isDeleteGym);
  };
  return (
    <>
      <S.Wrapper>
        <S.Title>
          수업 일정과 장소를{'\n'}
          입력해 주세요
        </S.Title>
        <S.SubTitle>수업 일정</S.SubTitle>
        <S.Description>
          수업이 있는 요일을 선택하고 수업 시간을 설정해 주세요
        </S.Description>
        <S.CheckBoxWrapper>
          <CheckBoxAtom
            id=""
            checked={isSameState}
            onClick={() => setIsSameState(checked => !checked)}
            label="수업 시간이 항상 같아요"
          />
        </S.CheckBoxWrapper>
        {isSameState ? <SameSchedule /> : <DifferentSchedule />}
        <Flex direction="column" gap={12}>
          <S.SubTitle>수업 장소</S.SubTitle>
          <Flex direction="column" gap={8}>
            {/* <Flex gap={8}>
              <RadioItem title="항상 같아요" isSelected fullWidth />
              <RadioItem title="그때 그때 달라요" fullWidth />
            </Flex> */}
            {gymState && isDeleteGym ? (
              <LocationContainer>
                <StyledImage
                  src={
                    gymState?.images && gymState.images.length > 0
                      ? gymState.images[0].resizedLink
                      : '/images/sns-thumbnail.png'
                  }
                ></StyledImage>
                <TextContainer>
                  <LocationTitle>{gymState.name}</LocationTitle>
                  <LocationSubTitle>{gymState.address}</LocationSubTitle>
                </TextContainer>
                <CloseButton onClick={handleDeleteGym}></CloseButton>
              </LocationContainer>
            ) : (
              <Input
                type="text"
                placeholder="소속 지점 등 수업 장소명을 입력해 주세요"
                onClick={() => navigate(PATH.ONBOARD_GYM_SEARCH)}
              />
            )}
          </Flex>
        </Flex>
      </S.Wrapper>
      <FloatButtonContainer>
        <Button onClick={handleNext} disabled={nextButtonDisabled}>
          다음으로
        </Button>
      </FloatButtonContainer>
    </>
  );
}

const StyledImage = styled.img`
  width: 50px; /* 이미지 크기 조정 */
  height: 50px; /* 이미지 크기 조정 */
  border-radius: 50%; /* 원형 이미지 (선택 사항) */
  object-fit: cover; /* 이미지 비율 유지 */
`;

const LocationTitle = styled.div`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.gray15};
`;

const LocationSubTitle = styled.div`
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.gray100};
`;

const LocationContainer = styled.div`
  display: flex;
  align-items: center; /* 수직 가운데 정렬 */
  justify-content: space-between; /* 좌우 끝 정렬 */
  padding: 10px; /* 여백 */
`;

const TextContainer = styled.div`
  flex: 1; /* 남는 공간을 차지하도록 설정 */
  margin: 0 20px; /* 좌우 여백 */
  text-align: left; /* 왼쪽 정렬 */
`;

const CloseButton = styled.button`
  color: #999; /* 회색 텍스트 */
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px; /* 버튼 크기 조정 */
  height: 30px; /* 버튼 크기 조정 */
  border-radius: 50%; /* 원형 버튼 */
  position: relative; /* 가상 요소 위치 설정 */
  transition:
    background-color 0.3s,
    color 0.3s;

  /* X 모양 생성 */
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 14px;
    background: currentColor; /* 텍스트 색상에 맞춤 */
  }

  &::before {
    transform: rotate(45deg); /* X 모양 */
  }

  &::after {
    transform: rotate(-45deg); /* X 모양 */
  }

  &:hover {
    background-color: #f0f0f0; /* hover 시 배경 색상 */
    color: #333; /* hover 시 텍스트 색상 */
  }

  &:focus {
    outline: none;
    background-color: #f0f0f0; /* focus 시 배경 색상 */
    color: #333; /* focus 시 텍스트 색상 */
  }
`;
